<template>
  <div class="card is-smallshadow mt-2 mb-2 relative">
    <div
      v-if="actionInProgress"
      class="loading-overlay">
      <div class="loading-spinner-wrapper is-embeded">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="loaded"
      class="card-body px-3 pt-3">
      <h2 :class="{ 'heading-small mb-1': true, 'line-height-small': !hasValidAccount}">
        <template v-if="hasValidAccount">
          {{ $t('settlements.yourCurrentPackage') }}
          <small
            v-if="!currentPackage || !currentPackage.auto_renew"
            :class="expiresClass">
            ({{ $t('settlements.expiresInDays', { daysAmount: (hasValidAccount ? accountValidDays : '-') }) }})
          </small>
        </template>
        <template v-else>
          <span class="text-danger">{{ $t('settlements.inactiveAccount') }}</span><br>
          <small class="text-bold">{{ $t('settlements.selectPlan') }}</small>
        </template>
      </h2>
      <div class="columns-wrapper">
        <div class="column-1 flex flex-column">
          <span class="text-size-medium">
            {{ $t('settlements.currentSubscription') }}: <strong>{{ packageName }}</strong>
          </span>
          <span class="text-size-medium">
            {{ $t('settlements.expiresOn') }}:
            <strong v-if="currentPackage && currentPackage.auto_renew"> -</strong>
            <strong v-else :class="expiresClass"> {{ humanDateFormat(accountValidTo) }}</strong>
          </span>
          <small class="text-grey-light">{{ $t('settlements.allPricesAreNet') }}.</small>

          <div class="mt-2">
            <f-form-radio
              v-for="option in renewalPeriodOptions"
              :key="'option-' + option.months"
              :value="option.months"
              :is-minimal="true"
              v-model="renewalPeriod"
              class="mb-2 mb-3-ms">
              {{ $tc('time.timeElapsedMonth', option.months) }}
              <f-badge
                v-if="option.discount"
                :theme="option.discount > 5 ? 'success' : 'primary'">
                {{ $t('settlements.percentOff', {percentAmount: option.discount}) }}
              </f-badge>
            </f-form-radio>
          </div>
        </div>
        <f-account-package
          v-if="!showAllPackages"
          class="column-2"
          :is-current="true"
          :is-minimal="true"
          :is-purchase-disabled="actionInProgress"
          :package-option="currentPackage"
          :renewal-period="renewalPeriod"
          :show-all-callback="setShowAllPackages"
          :subscribe-callback="renewPackage"/>
        <template v-else>
          <f-account-package
            v-for="(item, itemIndex) in availablePackages"
            :key="'package-wrapper-' + itemIndex"
            :class="'column-' + (itemIndex + 2)"
            :is-current="currentPackage && (item.id === currentPackage.id)"
            :is-minimal="false"
            :is-purchase-disabled="actionInProgress"
            :package-option="item"
            :renewal-period="renewalPeriod"
            :subscribe-callback="renewPackage" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';
import FAccountPackage from '@/components/layout/FAccountPackage.vue'

export default {
  name: 'account-package-details',
  components: {
    'f-account-package': FAccountPackage
  },
  props: {
    accountValidDays: {
      default: 0,
      type: Number
    },
    availablePackages: {
      default: () => [],
      type: Array
    },
    currentPackageId: {
      default: 0,
      type: Number
    },
    hasValidAccount: {
      default: false,
      type: Boolean
    }
  },
  computed: {
    currentPackage () {
      return this.availablePackages.find(item => item.id === this.currentPackageId);
    },
    expiresClass () {
      if (this.accountValidDays > 30 || !this.hasValidAccount) {
        return '';
      }
      if (this.accountValidDays > 10) {
        return 'text-warning';
      }

      return 'text-danger';
    }
  },
  data () {
    return {
      accountValidTo: null,
      loaded: false,
      packageName: null,
      renewalPeriod: 1,
      renewalPeriodOptions: [{
        discount: 0,
        months: 1
      }, {
        discount: 5,
        months: 6
      }, {
        discount: 10,
        months: 12
      }],
      showAllPackages: false,
      userType: null,
      actionInProgress: false
    };
  },
  mounted () {
    this.accountValidTo = this.$store.getters['getUserAccountValidTo'];
    this.packageName = this.$store.getters['getUserPackageName'];
    this.userType = this.$store.getters['getUserType'];

    if (!this.hasValidAccount) {
      this.showAllPackages = true;
    }

    this.loaded = true;
  },
  methods: {
    humanDateFormat (inputDate) {
      if (!this.hasValidAccount) {
        return '-';
      }
      return CalendarUtils.humanDateFormat(inputDate, true);
    },
    renewPackage (packageOption) {
      this.actionInProgress = true;
      if (packageOption.id === 1) {
        this.purchaseBasicPackage(packageOption);
        return;
      }

      this.purchasePayiedPackage(packageOption);
    },
    purchasePayiedPackage (packageOption) {
      FormUtils.submitInBackground(this, {
        endpoint: '/api/adviser/packages/pay',
        params: {
          package_id: packageOption.id,
          months: this.renewalPeriod
        },
        successAction: (response) => {
          if (response.payment_url) {
            Vue.swal({
              title: this.$t('settlements.purchasePlanSuccessTitle'),
              html: this.$t('settlements.purchasePlanSuccessText'),
              icon: 'info',
              confirmButtonText: 'OK',
              buttonsStyling: true,
              showCancelButton: true,
              cancelButtonText: this.$t('ui.cancel'),
              customClass: {
                actions: 'actions-order',
                cancelButton: 'order-1',
                confirmButton: 'order-2'
              }
            }).then((result) => {
              if (result.value) {
                window.location.replace(response.payment_url);
              } else {
                this.actionInProgress = false;
              }
            })
          } else {
            this.showPaymentUrlError();
          }
        },
        failAction: () => {
          this.showPaymentUrlError();
          this.actionInProgress = false;
        },
        cancelAction: () => {
          this.actionInProgress = false;
        }
      });
    },
    showPaymentUrlError () {
      Vue.swal({
        title: this.$t('ui.errorOccurred'),
        html: this.$t('settlements.purchasePlanErrorText'),
        icon: 'warning',
        confirmButtonText: 'OK',
        buttonsStyling: true,
      })
    },
    purchaseBasicPackage (packageOption) {
      FormUtils.submitWithConfirm(this, {
        endpoint: '/api/adviser/packages/pay',
        formData: {
          package_id: packageOption.id,
          months: this.renewalPeriod
        },
        successTitle: this.$t('settlements.packageChange'),
        successText: this.$t('settlements.yourCurrentPackageIs', { packageName: packageOption.name }),
        confirmTitle: this.$t('settlements.packageChange'),
        confirmText: this.$t('settlements.purchaseBasicConfirmText', { packageName: packageOption.name }),
        confirmButtonText: this.$t('ui.yes'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('settlements.purchaseBasicPackageErrorText'),
        confirmCustomClass: {
          actions: 'actions-order',
          cancelButton: 'order-1',
          confirmButton: 'order-2'
        },
        successAction: () => {
          window.location.reload();
        },
        failAction: () => {
          this.actionInProgress = false;
        },
        cancelAction: () => {
          this.actionInProgress = false;
        }
      });
    },
    setShowAllPackages () {
      Vue.set(this, 'showAllPackages', true);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
.columns-wrapper {
  display: flex;
  gap: 30px;

  .column-1,
  .column-2,
  .column-3,
  .column-4 {
    width: calc(25% - 22px);
  }
}

@media screen and (max-width: $media-desktop-s) {
  .columns-wrapper {
    gap: 20px;
    .column-1,
    .column-2,
    .column-3
    ,.column-4 {
      width: calc(25% - 15px);
    }
  }
}

@media screen and (max-width: $media-tablet-l) {
  .columns-wrapper {
    flex-wrap: wrap;
    gap: 20px 15px;

    .column-2,
    .column-3,
    .column-4 {
      width: calc((100% / 3) - 10px);
    }
    .column-1 {
      width: 100%
    }
  }
}

@media screen and (max-width: $media-tablet-xs) {
  .columns-wrapper {
    gap: 25px 15px;
    .column-2,
    .column-3
    ,.column-4 {
      width: 100%;
    }
  }
}
</style>
